// Token管理工具类
export class TokenManager {
  private static readonly DEFAULT_TOKEN_KEY = 'token';
  private static readonly DEFAULT_REFRESH_TOKEN_KEY = 'refresh_token';
  private static readonly TOKEN_PARAM = 'token';

  // 当前使用的token key（可以通过setTokenKey方法修改）
  private static tokenKey = this.DEFAULT_TOKEN_KEY;
  private static refreshTokenKey = this.DEFAULT_REFRESH_TOKEN_KEY;

  // 设置token key（用于不同微服务使用不同的key）
  static setTokenKey(tokenKey: string, refreshTokenKey?: string): void {
    this.tokenKey = tokenKey;
    this.refreshTokenKey = refreshTokenKey || `${tokenKey}_refresh`;
  }

  // 重置为默认token key
  static resetTokenKey(): void {
    this.tokenKey = this.DEFAULT_TOKEN_KEY;
    this.refreshTokenKey = this.DEFAULT_REFRESH_TOKEN_KEY;
  }

  // 获取token
  static getToken(): string | null {
    return localStorage.getItem(this.tokenKey) || sessionStorage.getItem(this.tokenKey);
  }

  // 从URL获取token
  static getTokenFromUrl(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(this.TOKEN_PARAM);
  }

  // 从存储获取token
  static getTokenFromStorage(): string | null {
    return localStorage.getItem(this.tokenKey) || sessionStorage.getItem(this.tokenKey);
  }

  // 获取当前token（优先从URL，然后从存储）
  static getCurrentToken(): string | null {
    const urlToken = this.getTokenFromUrl();
    if (urlToken) {
      this.setToken(urlToken);
      this.clearUrlToken();
      return urlToken;
    }

    const fromKey = this.getTokenFromStorage();
    if (fromKey) return fromKey;

    /**
     * CRM 等业务子域使用 tokenKey=crm_token；msLogin iframe 往往先写入主票 mslogin_token。
     * AuthGuard 的 cachedToken 会读到 mslogin_token，若此处不回落则 checkAuth 永远无票 → 卡在「正在验证登录…」。
     */
    if (this.tokenKey === "crm_token") {
      const ms =
        localStorage.getItem("mslogin_token") ||
        sessionStorage.getItem("mslogin_token");
      if (ms) {
        this.setToken(ms);
        return ms;
      }
    }

    return null;
  }

  // 清除URL中的token参数
  static clearUrlToken(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete(this.TOKEN_PARAM);
    window.history.replaceState({}, "", url.toString());
  }

  // 生成带token的服务URL
  static generateServiceUrl(serviceUrl: string, _serviceName: string): string {
    const token = this.getCurrentToken();
    if (!token) {
      return "";
    }
    const url = new URL(serviceUrl);
    url.searchParams.set(this.TOKEN_PARAM, token);
    return url.toString();
  }

  // 设置token
  static setToken(token: string, persistent: boolean = true): void {
    if (persistent) {
      localStorage.setItem(this.tokenKey, token);
      sessionStorage.removeItem(this.tokenKey);
    } else {
      sessionStorage.setItem(this.tokenKey, token);
      localStorage.removeItem(this.tokenKey);
    }
  }

  // 清除token
  static clearToken(): void {
    localStorage.removeItem(this.tokenKey);
    sessionStorage.removeItem(this.tokenKey);
  }

  // 获取刷新token
  static getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey) || sessionStorage.getItem(this.refreshTokenKey);
  }

  // 设置刷新token
  static setRefreshToken(token: string, persistent: boolean = true): void {
    if (persistent) {
      localStorage.setItem(this.refreshTokenKey, token);
      sessionStorage.removeItem(this.refreshTokenKey);
    } else {
      sessionStorage.setItem(this.refreshTokenKey, token);
      localStorage.removeItem(this.refreshTokenKey);
    }
  }

  // 清除刷新token
  static clearRefreshToken(): void {
    localStorage.removeItem(this.refreshTokenKey);
    sessionStorage.removeItem(this.refreshTokenKey);
  }

  // 清除所有认证信息
  static clearAll(): void {
    this.clearToken();
    this.clearRefreshToken();
  }

  // 检查token是否存在
  static hasToken(): boolean {
    return this.getToken() !== null;
  }

  // 检查token是否过期（简单实现）
  static isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;

    try {
      // 简单的JWT token解析（实际项目中应该使用专门的库）
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Date.now() / 1000;
      return payload.exp < now;
    } catch {
      return true;
    }
  }
}
